import { useEffect, useState } from "react";
import { Phone, Menu, X, Languages } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/LanguageContext";
import { PHONE_DISPLAY, PHONE_TEL } from "@/data/site";

const LINKS = [
  { href: "#services", key: "services", id: "services" },
  { href: "#work", key: "work", id: "our-work" },
  { href: "#process", key: "process", id: "how-it-works" },
  { href: "#area", key: "area", id: "service-area" },
  { href: "#quote", key: "quote", id: "get-a-quote" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const { t, toggle } = useLang();

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        solid ? "bg-steel-950/95 border-steel-700 backdrop-blur-md" : "bg-steel-950/70 border-transparent backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3 md:px-8 lg:px-12">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-3">
          <Logo />
          <span className="font-head text-xl font-extrabold uppercase leading-none tracking-tight text-steel-50 sm:text-2xl">
            RockinHwelding<span className="text-forge-light">.com</span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.id}`}
              className="font-head text-base font-semibold uppercase tracking-wide text-steel-400 transition-colors duration-200 hover:text-forge-light"
            >
              {t.nav[l.key]}
            </a>
          ))}
          <button
            type="button"
            onClick={toggle}
            aria-label={t.langAria}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 rounded-sm border border-steel-700 px-3 py-2 font-mono text-xs font-bold uppercase tracking-[0.14em] text-steel-400 transition-colors duration-200 hover:border-forge hover:text-forge-light"
          >
            <Languages size={15} />
            {t.langLabel}
          </button>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="header-call-btn"
            className="flex items-center gap-2 rounded-sm bg-forge px-5 py-2.5 font-head text-base font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-forge-light"
          >
            <Phone size={17} strokeWidth={2.4} />
            {PHONE_DISPLAY}
          </a>
        </nav>

        <div className="flex items-center gap-2 lg:hidden">
          <button
            type="button"
            onClick={toggle}
            aria-label={t.langAria}
            data-testid="lang-toggle-mobile"
            className="flex items-center gap-1.5 rounded-sm border border-steel-700 px-3 py-2 font-mono text-xs font-bold uppercase tracking-[0.14em] text-steel-50"
          >
            <Languages size={15} />
            {t.langLabel}
          </button>
          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="rounded-sm border border-steel-700 p-2 text-steel-50"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <nav data-testid="mobile-nav" className="border-t border-steel-700 bg-steel-950/98 px-4 pb-5 pt-2 lg:hidden">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.id}`}
              className="block border-b border-steel-800 py-3 font-head text-lg font-semibold uppercase tracking-wide text-steel-50"
            >
              {t.nav[l.key]}
            </a>
          ))}
        </nav>
      )}
    </header>
  );
};
